import React from 'react'
import ErrorFallback from './ErrorFallback'

export type ErrorBoundaryProps = {
  children: React.ReactNode
  fallback?: React.ReactNode
  on401?: () => void
  showErrorDetails?: boolean
}

export type ErrorBoundaryState = {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
    this.resetError = this.resetError.bind(this)
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error) {
    // Check for 401 error
    if (error.message.includes('401') && this.props.on401) {
      this.props.on401()
    }

    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error)
  }

  resetError() {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <ErrorFallback
            error={this.state.error}
            resetError={this.resetError}
            showDetails={this.props.showErrorDetails || import.meta.env.DEV}
          />
        )
      )
    }
    return this.props.children
  }
}
